import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SVG_ICONS } from "../../models/icons";
import { MasterService } from "@frontend/app/serivces/master.service";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { SharedModule } from "@frontend/app/shared/shared.module";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { MatSort } from "@angular/material/sort";
import { Subject, Subscription, tap } from "rxjs";
import { GlobalService } from "@frontend/app/serivces/global.service";

@Component({
  selector: "app-finance",
  imports: [CommonModule, MatPaginatorModule, SharedModule],
  templateUrl: "./finance.component.html",
  styleUrl: "./finance.component.scss",
})
export class FinanceComponent implements AfterViewInit, OnDestroy {
  private masterService = inject(MasterService);
  private globalService = inject(GlobalService);

  ngAfterViewInit() {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const name = `${data.FirstName || ""} ${data.MiddleName || ""} ${data.LastName || ""}`.toLowerCase();
      const mobile = `${data.MobileNo || ""}`;
      return name.includes(filter) || mobile.includes(filter);
    };

    this.paginator.page.pipe(tap(() => this.getFinanceData(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active, this.sort.direction))).subscribe();
    this.sort.sortChange.subscribe(() => {
      this.getFinanceData(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active, this.sort.direction);
    });
    this.searchSubscription = this.globalService.setupDebouncedSearch(this.searchSubject, searchValue => {
      this.searchString = searchValue;
      this.paginator.pageIndex = 0;
      this.getFinanceData(1, this.paginator.pageSize, this.sort.active, this.sort.direction);
    });

    this.getFinanceData(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active, this.sort.direction);
  }

  //#region  variables
  readonly SVG_ICONS = SVG_ICONS;
  financeData: any;
  displayedColumns: string[] = ["FirstName", "PackageName", "ClassName", "CenterName", "Date", "FinalAmount", "Status", "Receipt"];
  dataSource = new MatTableDataSource();
  length = 0;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  searchString: string = "";
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  //#endregion

  getFinanceData(pageIndex: number, pageSize: number, sortColumn: string, sortDirection: string) {
    const sort = sortColumn ? `ORDER BY ${sortColumn} ${sortDirection}` : "ORDER BY FirstName";
    this.masterService.getFinanceMaster(pageIndex, pageSize, sort.toString(), sessionStorage.getItem("selectedCenter") || "", this.searchString).subscribe((res: any) => {
      const { Table, TotalCount } = res;
      this.length = TotalCount;
      this.financeData = Table;
      const centeID = sessionStorage.getItem("selectedCenter") || "";

      // Sort the data to show paid entries first
      const sortedData = [...this.financeData].sort((a, b) => {
        const aIsPaid = this.getActiveClassStatus(a.Student) === "Paid" ? 0 : 1;
        const bIsPaid = this.getActiveClassStatus(b.Student) === "Paid" ? 0 : 1;
        return aIsPaid - bIsPaid;
      });

      this.dataSource.data = sortedData;
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim();
    this.searchSubject.next(filterValue);
  }

  getActiveClassIds(student: any): string {
    return (
      student?.classEnrollments
        ?.filter((c: any) => c.IsActive)
        .map((c: any) => c.ClassID)
        .join(", ") || "-"
    );
  }

  getActiveClassNames(student: any): string {
    return (
      student?.classEnrollments
        ?.filter((c: any) => c.IsActive)
        .map((c: any) => c.classDetails?.ClassName)
        .join(", ") || "-"
    );
  }

  getActiveClassStatus(student: any): "Paid" | "Pending" {
    const activeClass = student?.classEnrollments?.find((c: any) => c.IsActive);
    return activeClass?.IsPaymentDone ? "Paid" : "Pending";
  }
  downloadReceipt(item: any) {
    const studentData = item;
    const latestReceipt = item.PaymentLedger?.sort((a: any, b: any) => new Date(b.Date).getTime() - new Date(a.Date).getTime())[0];
    const latestSubscription = item.packageSubscriptions?.sort((a: any, b: any) => new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime())[0];
    const activeClass = item.classEnrollments?.find((x: any) => x.IsActive);

    if (!latestReceipt || !latestSubscription) {
      console.error("Missing critical data to generate receipt.", { latestReceipt, latestSubscription });
      alert("Could not generate receipt: Missing payment or subscription data.");
      return; // Stop execution
    }

    const getPaymentModeName = (ptid: number | null): string => {
      switch (ptid) {
        case 1:
          return "Cash";
        case 2:
          return "Card";
        case 3:
          return "UPI";
        // Add other payment modes as needed
        default:
          return "N/A"; // Handle null or other values
      }
    };

    // --- 3. PDF GENERATION ---
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const brandColor = [41, 128, 185];
    const secondaryColor = [52, 152, 219];
    const successColor = [46, 204, 113];
    const textColor = [44, 62, 80];

    // --- Header ---
    doc.setFillColor(brandColor[0], brandColor[1], brandColor[2]);
    doc.rect(0, 0, pageWidth, 35, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(studentData.center?.CenterName || "FITNESS CENTER", pageWidth / 2, 18, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Excellence in Fitness & Training", pageWidth / 2, 26, { align: "center" });

    // --- Title Bar ---
    doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.rect(0, 35, pageWidth, 12, "F");
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("PAYMENT RECEIPT", pageWidth / 2, 43, { align: "center" });

    // --- Receipt Info Box ---
    // --- Receipt Info Box ---
    // --- Receipt Info Box ---
    // --- Receipt Info Box ---
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.setFillColor(255, 255, 255);

    // Box Height
    doc.roundedRect(14, 55, pageWidth - 28, 28, 2, 2, "FD");

    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFontSize(10);

    // --- COORDINATES CONFIGURATION ---
    const row1Y = 63;
    const row2Y = 73;

    // LEFT COLUMN (Receipt & Invoice)
    const leftLabelX = 20;
    const leftValueX = 50; // Values start here (after the colon)

    // RIGHT COLUMN (Date & Member ID)
    // Adjust rightLabelX if you need it further left/right
    const rightLabelX = pageWidth - 95;
    const rightValueX = pageWidth - 65; // Values start here (after the colon)

    // --- ROW 1 ---

    // 1. Receipt No. (Left)
    doc.setFont("helvetica", "normal");
    doc.text("Receipt No. :", leftLabelX, row1Y);
    doc.setFont("helvetica", "bold");
    doc.text(latestReceipt.FinalReceiptNumber || "-", leftValueX, row1Y);

    // 2. Payment Date (Right)
    doc.setFont("helvetica", "normal");
    doc.text("Payment Date :", rightLabelX, row1Y);
    doc.setFont("helvetica", "bold");
    doc.text(
      new Date(latestReceipt.Date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      rightValueX, // Now starts immediately after label
      row1Y
      // Removed { align: "right" } so it defaults to left-align at this X
    );

    // --- ROW 2 ---

    // 3. Invoice No. (Left)
    doc.setFont("helvetica", "normal");
    doc.text("Invoice No. :", leftLabelX, row2Y);
    doc.setFont("helvetica", "bold");
    doc.text(latestReceipt.InvoiceNumber || "-", leftValueX, row2Y);

    // 4. Member ID (Right)
    doc.setFont("helvetica", "normal");
    doc.text("Member ID :", rightLabelX, row2Y);
    doc.setFont("helvetica", "bold");
    doc.text(String(studentData.StudentID || "-"), rightValueX, row2Y);

    // --- Member Details ---
    let currentY = 95;
    // ... rest of code
    doc.setFontSize(12);
    // ... rest of your code
    doc.setFont("helvetica", "bold");
    doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
    doc.text("MEMBER DETAILS", 14, currentY);
    // ... rest of the code follows
    doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(14, currentY + 1, 55, currentY + 1);

    currentY += 10;
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Student Name :", 20, currentY);
    doc.text("Mobile No. :", 20, currentY + 8);

    doc.setFont("helvetica", "bold");
    const fullName = `${studentData.FirstName || ""}  ${studentData.MiddleName || ""} ${studentData.LastName || ""} `.trim();
    doc.text(fullName, 45, currentY);
    doc.text(studentData.MobileNo || "-", 45, currentY + 8);

    // --- Payment Details ---
    currentY += 25;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
    doc.text("PAYMENT DETAILS", 14, currentY);
    doc.setLineWidth(0.5);
    doc.line(14, currentY + 1, 58, currentY + 1);

    const packageAmount = latestSubscription.PackageAmount || 0;
    const cgst = latestSubscription.CGstTax || 0;
    const sgst = latestSubscription.SGstTax || 0;

    autoTable(doc, {
      startY: currentY + 5,
      head: [["DESCRIPTION", "AMOUNT"]],
      body: [
        [latestSubscription.packageDetails?.PackageName || "Membership Package", packageAmount.toFixed(2)],
        ["CGST", cgst.toFixed(2)],
        ["SGST", sgst.toFixed(2)],
        ["Assigned Class", activeClass?.classDetails?.ClassName || "N/A"],
      ],
      theme: "grid",
      margin: { left: 14, right: 14 },
      headStyles: {
        fillColor: [secondaryColor[0], secondaryColor[1], secondaryColor[2]],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 10,
        textColor: [textColor[0], textColor[1], textColor[2]],
      },
      columnStyles: {
        0: { halign: "left", cellPadding: 3 },
        1: { halign: "right", cellPadding: 3 },
      },
      didDrawCell: data => {
        // Remove vertical lines
        if (data.column.index === 1 && data.cell.section === "body") {
          doc.setDrawColor(255, 255, 255); // Set draw color to white to "erase" line
          doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
        }
      },
    });

    const tableEndY = (doc as any).lastAutoTable.finalY;

    // --- Total Amount Paid Box ---
    doc.setFillColor(successColor[0], successColor[1], successColor[2]);
    doc.roundedRect(14, tableEndY + 5, pageWidth - 28, 12, 2, 2, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL AMOUNT PAID", 20, tableEndY + 12.5);
    // Use 'Credit' from PaymentLedger, which is the amount paid
    const totalPaid = latestReceipt.Credit || 0;
    doc.text(`${totalPaid.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 18, tableEndY + 12.5, { align: "right" });

    // --- Payment Method ---
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    // Use helper function to get payment mode name from PTID
    doc.text(`Payment Mode : Cash`, 16, tableEndY + 25);

    // --- Watermark ---
    // doc.setFontSize(80);
    // doc.setFont("helvetica", "bold");
    // doc.setTextColor(230, 230, 230); // Use a light gray fill color
    // doc.text("PAID", pageWidth / 2, pageHeight / 1.8, {
    //   align: "center",
    //   angle: 45,
    // });

    // --- Footer ---
    const footerY = pageHeight - 30;
    doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.setLineWidth(0.3);
    doc.line(14, footerY, pageWidth - 14, footerY);

    doc.setFontSize(9);
    doc.setTextColor(127, 140, 141);
    doc.setFont("helvetica", "normal");
    doc.text("This is a computer-generated receipt and does not require a signature.", pageWidth / 2, footerY + 8, { align: "center" });

    doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
    doc.setFont("helvetica", "bold");
    doc.text("Thank you for your payment!", pageWidth / 2, footerY + 14, { align: "center" });

    doc.setFontSize(8);
    doc.setTextColor(149, 165, 166);
    doc.setFont("helvetica", "normal");
    doc.text("For queries, please contact: support@athleticafitness.com", pageWidth / 2, footerY + 19, { align: "center" });

    // --- 4. SAVE DOCUMENT ---
    // Use FinalReceiptNumber for the file name
    doc.save(`Receipt-${latestReceipt.FinalReceiptNumber}.pdf`);
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }
}
